import { ReactNode, useEffect, useRef, useState } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  resizable?: boolean;
}

/**
 * Modal dialog — accessible with focus trap and escape key handling.
 * Supports optional resizable mode with maximize button for document viewers.
 */
export function Modal({ isOpen, onClose, title, children, footer, resizable }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [maximized, setMaximized] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      dialog.showModal();
    } else {
      dialog.close();
      setMaximized(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (maximized) {
          setMaximized(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, maximized]);

  if (!isOpen) return null;

  let className = 'modal';
  if (resizable) className += ' modal-resizable';
  if (maximized) className += ' modal-maximized';

  return (
    <dialog ref={dialogRef} className={className} aria-labelledby="modal-title" onClose={onClose}>
      <div className="modal-content">
        <header className="modal-header">
          <h2 id="modal-title">{title}</h2>
          <div className="modal-header-actions">
            {resizable && (
              <button
                className="modal-maximize"
                onClick={() => setMaximized(!maximized)}
                aria-label={maximized ? 'Ripristina dimensioni' : 'Massimizza'}
                title={maximized ? 'Ripristina dimensioni' : 'Massimizza'}
              >
                {maximized ? '⧉' : '⬜'}
              </button>
            )}
            <button className="modal-close" onClick={onClose} aria-label="Chiudi">
              ×
            </button>
          </div>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </dialog>
  );
}
